import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useSocket } from '../context/SocketContext';

// The bell now reads the DURABLE inbox (design §1): GET /notifications is the source of truth,
// the socket `inbox:new` is a nudge to re-fetch, and a 60s poll is the socket-down backstop.
// Read state is user-driven only (click / dismiss / mark-all).

const CATS = {
  orders:      { label: 'Orders',      icon: '📦' },
  procurement: { label: 'Procurement', icon: '🚚' },
  finance:     { label: 'Finance',     icon: '💰' },
  samples:     { label: 'Samples',     icon: '🧵' },
  hr:          { label: 'HR',          icon: '🧑‍💼' },
  quality:     { label: 'Quality',     icon: '✅' },
  production:  { label: 'Production',   icon: '⚙️' },
  system:      { label: 'System',      icon: '🛠' },
  other:       { label: 'Other',       icon: '🔔' },
};
const catKey = (n) => (n.category && CATS[n.category] ? n.category : 'other');
const fmtTime = (t) => { try { return new Date(t).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

export default function NotificationBell() {
  const { connected } = useSocket();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [fetchOk, setFetchOk] = useState(null);   // null=loading, true=ok, false=failed
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');    // 'all' | 'action'
  const [expanded, setExpanded] = useState({});    // category → bool
  const wrap = useRef(null);

  const fetchInbox = useCallback(async () => {
    try {
      const [list, count] = await Promise.all([
        api.get('/notifications?limit=50'),
        api.get('/notifications/unread-count'),
      ]);
      setItems(list.data?.data || []);
      setUnread(count.data?.data?.unread || 0);
      setFetchOk(true);
    } catch (e) {
      setFetchOk(false);
      console.error('NotificationBell: fetch failed', e);
    }
  }, []);

  // Mount fetch + inbox:new nudge + 60s backstop poll (design §1).
  useEffect(() => {
    fetchInbox();
    const onNudge = () => fetchInbox();
    window.addEventListener('inbox:new', onNudge);
    const t = setInterval(fetchInbox, 60000);
    return () => { window.removeEventListener('inbox:new', onNudge); clearInterval(t); };
  }, [fetchInbox]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const markRead = async (id) => {
    setItems(prev => prev.map(n => n.id === id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n));
    setUnread(u => Math.max(0, u - 1));
    // write-error-ok: optimistic read-mark; the next inbox poll restores the server's true state
    try { await api.patch(`/notifications/${id}/read`); } catch { /* refetch will reconcile */ }
  };
  const markAll = async () => {
    setItems(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    setUnread(0);
    // write-error-ok: optimistic read-all; fetchInbox() on the next line restores the true state
    try { await api.patch('/notifications/read-all'); } catch { /* */ }
    fetchInbox();
  };
  const onClick = (n) => { if (!n.read_at) markRead(n.id); setOpen(false); if (n.link) navigate(n.link); };

  const actionable = items.filter(n => n.requires_action && !n.read_at);
  const shown = filter === 'action' ? actionable : items;
  // Group the non-action list by category (the ⚡ action block is rendered separately).
  const groups = {};
  shown.forEach(n => { if (filter === 'all' && n.requires_action && !n.read_at) return; const k = catKey(n); (groups[k] = groups[k] || []).push(n); });

  const status = fetchOk === null ? { t: 'Loading…', c: '#aaa', d: '#aaa' }
    : fetchOk === false ? { t: "Can't reach notifications — retrying", c: '#A32D2D', d: '#E24B4A' }
    : connected ? { t: 'Up to date · live', c: '#1D9E75', d: '#1D9E75' }
    : { t: 'Up to date · checking every minute', c: '#854F0B', d: '#EF9F27' };

  const Row = (n) => (
    <div key={n.id} onClick={() => onClick(n)} style={{ padding: '9px 16px', borderBottom: '1px solid #f5f5f5', display: 'flex', gap: 10, alignItems: 'flex-start', cursor: n.link ? 'pointer' : 'default', background: n.read_at ? '#fff' : '#F7FAFF' }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: n.read_at ? '#ddd' : (n.requires_action ? '#E24B4A' : '#378ADD'), flexShrink: 0, marginTop: 5 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {n.title && <div style={{ fontSize: 12, fontWeight: 700, color: '#222' }}>{n.title}</div>}
        <div style={{ fontSize: 12, color: '#444', lineHeight: 1.4 }}>{n.message}</div>
        <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>{fmtTime(n.created_at)}</div>
      </div>
      {!n.read_at && <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }} title="Mark read" style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: 14, padding: '0 4px', flexShrink: 0 }}>×</button>}
    </div>
  );

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: 18 }}>🔔</span>
        {unread > 0 && (
          <span style={{ position: 'absolute', top: 0, right: 0, background: '#E24B4A', color: 'white', borderRadius: '50%', minWidth: 16, height: 16, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, padding: '0 3px' }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{ position: 'fixed', top: 60, right: 20, width: 380, maxHeight: 520, overflowY: 'auto', background: 'white', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #eee', zIndex: 9999 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>Notifications {unread > 0 && `(${unread})`}</span>
            {items.some(n => !n.read_at) && <button onClick={markAll} style={{ fontSize: 12, color: '#3B3A8F', background: 'none', border: 'none', cursor: 'pointer' }}>Mark all read</button>}
          </div>

          {/* Filter toggle (design §5) */}
          <div style={{ display: 'flex', gap: 6, padding: '8px 16px', borderBottom: '1px solid #f2f2f2' }}>
            {[['all', 'All'], ['action', `⚡ Action needed${actionable.length ? ` (${actionable.length})` : ''}`]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 14, cursor: 'pointer', border: '1px solid ' + (filter === k ? '#3B3A8F' : '#ddd'), background: filter === k ? '#EEEDF8' : '#fff', color: filter === k ? '#3B3A8F' : '#888' }}>{l}</button>
            ))}
          </div>

          {items.length === 0 ? (
            <div style={{ padding: 28, textAlign: 'center', color: '#aaa', fontSize: 13 }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>🔔</div>You're all caught up
            </div>
          ) : (
            <>
              {/* ⚡ ACTION NEEDED — always on top in All view */}
              {filter === 'all' && actionable.length > 0 && (
                <div>
                  <div style={{ padding: '8px 16px', background: '#FCEBEB', color: '#A32D2D', fontSize: 11, fontWeight: 800, letterSpacing: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                    <span>⚡ ACTION NEEDED</span><span>{actionable.length}</span>
                  </div>
                  {actionable.map(Row)}
                </div>
              )}
              {/* Category groups (collapsible) */}
              {Object.keys(groups).sort().map(k => {
                const g = groups[k]; const meta = CATS[k]; const isOpen = expanded[k];
                return (
                  <div key={k}>
                    <div onClick={() => setExpanded(e => ({ ...e, [k]: !e[k] }))} style={{ padding: '8px 16px', background: '#F7F6F3', color: '#555', fontSize: 11, fontWeight: 800, letterSpacing: 0.5, display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}>
                      <span>{meta.icon} {meta.label.toUpperCase()}</span>
                      <span>{isOpen ? '▲' : '▼'} {g.length}</span>
                    </div>
                    {isOpen && g.map(Row)}
                  </div>
                );
              })}
              {filter === 'action' && actionable.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: '#aaa', fontSize: 12 }}>Nothing needs your action.</div>
              )}
            </>
          )}

          {/* Honest status line — reports the FETCH, not the socket (design §1) */}
          <div style={{ padding: '10px 16px', borderTop: '1px solid #eee', textAlign: 'center', position: 'sticky', bottom: 0, background: '#fff' }}>
            <span style={{ fontSize: 11, color: status.c, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: status.d, display: 'inline-block' }} />
              {status.t}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
